/**
 * <hero-video-toggle> — pause/play control for ambient background videos.
 * Wraps one <button>; controls every <video> inside the element named by
 * the `target` attribute (an id). Starts paused when the visitor prefers
 * reduced motion. Labels come from data-play-label / data-pause-label.
 */
class HeroVideoToggle extends HTMLElement {
  #button = null;
  #videos = [];
  #onClick = () => this.#toggle();
  #onState = () => this.#render();

  connectedCallback() {
    this.#button = this.querySelector('button');
    const scope = document.getElementById(this.getAttribute('target') || '');
    this.#videos = scope ? Array.from(scope.querySelectorAll('video')) : [];
    if (!this.#button || this.#videos.length === 0) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#videos.forEach((video) => {
        video.removeAttribute('autoplay');
        video.pause();
      });
    }

    this.#button.addEventListener('click', this.#onClick);
    this.#videos.forEach((video) => {
      video.addEventListener('play', this.#onState);
      video.addEventListener('pause', this.#onState);
    });
    this.#render();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#onClick);
    this.#videos.forEach((video) => {
      video.removeEventListener('play', this.#onState);
      video.removeEventListener('pause', this.#onState);
    });
  }

  #visibleVideos() {
    return this.#videos.filter((video) => video.offsetParent !== null && !video.hasAttribute('data-reel-standby'));
  }

  #toggle() {
    const visible = this.#visibleVideos();
    const anyPlaying = visible.some((video) => !video.paused);
    visible.forEach((video) => {
      if (anyPlaying) {
        video.pause();
      } else {
        video.play().catch(() => {});
      }
    });
  }

  #render() {
    const paused = !this.#visibleVideos().some((video) => !video.paused);
    this.#button.setAttribute('aria-label', paused ? this.dataset.playLabel : this.dataset.pauseLabel);
    this.toggleAttribute('data-paused', paused);
  }
}

if (!customElements.get('hero-video-toggle')) {
  customElements.define('hero-video-toggle', HeroVideoToggle);
}
